// Small presentational components used by the survey engine.
import { useRef, useEffect } from 'react';

// Accessible modal: locks background scroll, focuses the first control on open,
// traps Tab, restores focus on close, and supports Escape (when onEscape given).
export function Modal({ titleId, onEscape, children }) {
  const ref = useRef(null);
  const escRef = useRef(onEscape);
  escRef.current = onEscape;
  useEffect(() => {
    const root = ref.current;
    const prevFocus = document.activeElement;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const focusables = () => Array.from(root.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'))
      .filter((el) => !el.disabled && el.offsetParent !== null);
    focusables()[0]?.focus();
    function onKey(e) {
      if (e.key === 'Escape' && escRef.current) { e.preventDefault(); escRef.current(); return; }
      if (e.key !== 'Tab') return;
      const f = focusables();
      if (!f.length) return;
      const i = f.indexOf(document.activeElement);
      if (e.shiftKey && i <= 0) { e.preventDefault(); f[f.length - 1].focus(); }
      else if (!e.shiftKey && i === f.length - 1) { e.preventDefault(); f[0].focus(); }
    }
    root.addEventListener('keydown', onKey);
    return () => {
      root.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      if (prevFocus && prevFocus.focus) prevFocus.focus();
    };
  }, []);
  return (
    <div className="modal-overlay" role="dialog" aria-modal="true" aria-labelledby={titleId} ref={ref}>
      <div className="modal">{children}</div>
    </div>
  );
}

export function ProgressBar({ value }) {
  return (
    <div className="progress" aria-hidden="true">
      <div className="progress__bar" style={{ width: `${Math.round(value * 100)}%` }} />
    </div>
  );
}

const ANCHORS = ['Strongly disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly agree'];
const LIKERT_OPTS = [1, 2, 3, 4, 5].map((n) => ({ value: n, label: ANCHORS[n - 1] }));

// Accessible radio group: roving tabindex, arrow-key navigation, labelled by the
// question stem, and aria-invalid when flagged.
function RadioGroup({ className, optClass, labelId, invalid, options, value, onChange, renderOption }) {
  const ref = useRef(null);
  const selIndex = options.findIndex((o) => o.value === value);
  function onKeyDown(e) {
    const nav = ['ArrowDown', 'ArrowRight', 'ArrowUp', 'ArrowLeft', 'Home', 'End'];
    if (!nav.includes(e.key)) return;
    e.preventDefault();
    const btns = Array.from(ref.current.querySelectorAll('[role="radio"]'));
    const cur = btns.indexOf(document.activeElement);
    let ni;
    if (e.key === 'Home') ni = 0;
    else if (e.key === 'End') ni = btns.length - 1;
    else if (e.key === 'ArrowDown' || e.key === 'ArrowRight') ni = cur < 0 ? 0 : (cur + 1) % btns.length;
    else ni = cur < 0 ? btns.length - 1 : (cur - 1 + btns.length) % btns.length;
    btns[ni].focus();
    onChange(options[ni].value);
  }
  return (
    <div ref={ref} className={className} role="radiogroup" aria-labelledby={labelId} aria-invalid={invalid || undefined} onKeyDown={onKeyDown}>
      {options.map((o, i) => {
        const sel = o.value === value;
        return (
          <button
            key={String(o.value)}
            type="button"
            role="radio"
            aria-checked={sel}
            tabIndex={(selIndex < 0 ? i === 0 : sel) ? 0 : -1}
            className={optClass + (sel ? ` ${optClass}--sel` : '')}
            onClick={() => onChange(o.value)}
          >
            {renderOption(o)}
          </button>
        );
      })}
    </div>
  );
}

export function Field({ item, value, onChange, invalid }) {
  const labelId = 'lbl-' + item.key;
  const errId = 'err-' + item.key;
  return (
    <div className={'field' + (invalid ? ' field--invalid' : '')}>
      <div className="field__stem" id={labelId}>{item.stem}</div>
      {item.type === 'number' && (
        <input
          className="num"
          type="number"
          inputMode="numeric"
          min={item.min}
          max={item.max}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Enter a number"
          aria-labelledby={labelId}
          aria-invalid={invalid || undefined}
          aria-describedby={invalid ? errId : undefined}
        />
      )}
      {item.type === 'single' && (
        <RadioGroup className="choices" optClass="choice" labelId={labelId} invalid={invalid}
          options={item.options} value={value} onChange={onChange}
          renderOption={(o) => (<><span className="choice__dot" /><span>{o.label}</span></>)} />
      )}
      {item.type === 'likert' && (
        <RadioGroup className="likert" optClass="likert__opt" labelId={labelId} invalid={invalid}
          options={LIKERT_OPTS} value={value} onChange={onChange}
          renderOption={(o) => (<><span className="likert__num">{o.value}</span><span className="likert__lbl">{o.label}</span></>)} />
      )}
      {invalid && <div className="field__err" id={errId}>Please answer this question.</div>}
    </div>
  );
}
